import React, { Component } from 'react';

// Ritmo de carga del conteo (sólo control/admin). Lee la traza silenciosa que deja cada valor
// que el operador pone en un campo, con la hora del servidor. La pregunta que responde es "¿se
// contó de verdad?": diez juegos cargados en un minuto no se contaron, se tipearon.
//
// Los umbrales viven acá y no en el backend: son una forma de mirar, no una regla del negocio,
// y conviene poder moverlos mientras se aprende cuánto tarda un conteo honesto.
const config = process.env;
const apiBase = () => config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;
const authHeaders = () => ({
    'access-token': localStorage.getItem('token'),
    'Content-Type': 'application/json',
});

const BURST_WINDOW_SECONDS = 60;
const DEFAULT_MIN_SECONDS = 10;
const ROLE_ADMIN = 1;

// 'YYYY-MM-DD HH:mm:ss' (hora de pared) -> segundos. Todas se leen igual, así que las
// diferencias son las reales.
export function wallClockToSeconds(value) {
    if (!value) return null;
    const t = Date.parse(String(value).replace(' ', 'T') + 'Z');
    return Number.isNaN(t) ? null : t / 1000;
}

// La mayor cantidad de cargas que entran en una ventana de `windowSeconds`.
export function maxBurst(entries, windowSeconds = BURST_WINDOW_SECONDS) {
    const times = entries.map((e) => wallClockToSeconds(e.enteredAt)).filter((t) => t != null);
    let best = 0;
    let start = 0;
    for (let end = 0; end < times.length; end++) {
        while (times[end] - times[start] >= windowSeconds) start++;
        best = Math.max(best, end - start + 1);
    }
    return best;
}

export function formatDuration(seconds) {
    if (seconds == null) return '—';
    if (seconds < 1) return '< 1 s';
    const s = Math.round(seconds);
    if (s < 60) return `${s} s`;
    const m = Math.floor(s / 60);
    const rest = s % 60;
    if (m < 60) return rest ? `${m} min ${rest} s` : `${m} min`;
    return `${Math.floor(m / 60)} h ${m % 60} min`;
}

// Arranca CERRADA (pedido del dueño): la tabla de Conteos es lo principal de la pantalla y la
// alerta igual se ve en el encabezado. Abierta/cerrada se recuerda por browser: es una
// preferencia de quien mira, no un dato. El storage puede no estar (modo privado, bloqueado),
// así que todo acceso va en try/catch.
const OPEN_KEY = 'stockCountPaceOpen';
const readOpen = () => {
    try {
        return localStorage.getItem(OPEN_KEY) === '1';
    } catch (e) {
        return false;
    }
};
const writeOpen = (open) => {
    try {
        localStorage.setItem(OPEN_KEY, open ? '1' : '0');
    } catch (e) {
        // sin storage, sólo no se recuerda
    }
};

const hora = (enteredAt) => (enteredAt ? String(enteredAt).slice(11, 19) : '—');

class StockCountPaceComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            open: readOpen(),
            date: '',
            minSeconds: DEFAULT_MIN_SECONDS,
            data: null,
            loading: true,
            error: null,
        };
    }

    componentDidMount() {
        this.load('');
    }

    load = async (date) => {
        this.setState({ loading: true, error: null });
        try {
            const query = date ? `?date=${encodeURIComponent(date)}` : '';
            const res = await fetch(apiBase() + 'stock/entry-log' + query, { headers: authHeaders() });
            const body = await res.json();
            if (body.error !== 0) throw new Error('entry-log');
            this.setState({ data: body.data, date: body.data.countDate, loading: false });
        } catch (e) {
            this.setState({ error: 'No se pudo cargar el ritmo de carga', loading: false });
        }
    }

    toggleOpen = () => {
        const open = !this.state.open;
        writeOpen(open);
        this.setState({ open });
    }

    onChangeMinSeconds = (value) => {
        const n = Number(value);
        this.setState({ minSeconds: Number.isFinite(n) && n >= 0 ? n : DEFAULT_MIN_SECONDS });
    }

    renderSummary(entries, summary, fast) {
        if (entries.length === 0) {
            return <p className="text-muted m-0">No hay cargas registradas para este día.</p>;
        }
        const burst = maxBurst(entries);
        const average = entries.length > 1 && summary.spanSeconds != null
            ? summary.spanSeconds / (entries.length - 1)
            : null;
        return (
            <div className="d-flex flex-wrap" style={{ gap: '1.5rem' }}>
                <div>
                    <small className="text-muted d-block">Cargas</small>
                    <strong>{summary.entries}</strong> <small className="text-muted">de {summary.products} juego(s)</small>
                </div>
                <div>
                    <small className="text-muted d-block">Entre</small>
                    <strong>{hora(summary.first)} y {hora(summary.last)}</strong>{' '}
                    <small className="text-muted">({formatDuration(summary.spanSeconds)})</small>
                </div>
                <div>
                    <small className="text-muted d-block">Promedio entre cargas</small>
                    <strong>{formatDuration(average)}</strong>
                </div>
                <div>
                    <small className="text-muted d-block">Más rápidas que {this.state.minSeconds} s</small>
                    <strong className={fast > 0 ? 'text-danger' : ''}>{fast}</strong>
                </div>
                <div>
                    <small className="text-muted d-block">Máximo en {BURST_WINDOW_SECONDS} s</small>
                    <strong className={burst >= 5 ? 'text-danger' : ''}>{burst} carga(s)</strong>
                </div>
            </div>
        );
    }

    render() {
        const { open, data, loading, error, date, minSeconds } = this.state;
        const entries = (data && data.entries) || [];
        const isFast = (e) => e.gapSeconds != null && e.gapSeconds < minSeconds;
        const fast = entries.filter(isFast).length;
        return (
            <div className="card card-small mb-4">
                <div className={'card-header d-flex justify-content-between align-items-center flex-wrap' + (open ? ' border-bottom' : '')}>
                    {/* Sólo el título pliega: los controles de la derecha viven en el mismo
                        encabezado y un click en ellos no tiene que cerrar la tarjeta. */}
                    <button
                        type="button"
                        className="btn btn-link p-0 m-0 mr-3 d-flex align-items-center text-reset"
                        style={{ textDecoration: 'none' }}
                        aria-expanded={open}
                        onClick={this.toggleOpen}
                    >
                        <i className="material-icons mr-1" style={{ color: '#818ea3', fontSize: 20 }}>{open ? 'expand_less' : 'expand_more'}</i>
                        <h6 className="m-0">Ritmo de carga</h6>
                        {/* Cerrada, el encabezado conserva la alerta: plegar no puede esconder
                            que hubo cargas imposibles. */}
                        {!open && !loading && data && (
                            <small className="ml-2 text-muted">
                                {data.countDate} · {entries.length} carga(s)
                                {fast > 0 && <span className="text-danger"> · {fast} más rápida(s) que {minSeconds} s</span>}
                            </small>
                        )}
                    </button>
                    {open && (
                    <div className="d-flex align-items-center flex-wrap">
                        <label className="m-0 mr-3">
                            <span className="mr-1">Día</span>
                            <input
                                type="date"
                                className="form-control form-control-sm d-inline-block"
                                style={{ width: 'auto' }}
                                value={date}
                                onChange={(e) => e.target.value && this.load(e.target.value)}
                            />
                        </label>
                        <label className="m-0">
                            <span className="mr-1">Marcar si pasan menos de</span>
                            <input
                                type="number"
                                min="0"
                                className="form-control form-control-sm d-inline-block"
                                style={{ width: 70 }}
                                value={minSeconds}
                                onChange={(e) => this.onChangeMinSeconds(e.target.value)}
                            />
                            <span className="ml-1">s</span>
                        </label>
                    </div>
                    )}
                </div>
                {open && (
                <div className="card-body">
                    {error && <div className="alert alert-danger">{error}</div>}
                    {loading ? <p className="m-0">Cargando…</p> : data && (
                        <>
                            {this.renderSummary(entries, data.summary, fast)}
                            {data.responsibles && data.responsibles.length > 0 && (
                                <p className="mt-2 mb-0"><small className="text-muted">Responsables anotados ese día: </small>
                                    <small><strong>{data.responsibles.join(' · ')}</strong></small></p>
                            )}
                        </>
                    )}
                </div>
                )}
                {open && !loading && entries.length > 0 && (
                    <div className="card-body p-0 pb-3" style={{ overflowX: 'auto' }}>
                        <table className="table mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0">Hora</th>
                                    <th className="border-0">Desde la anterior</th>
                                    <th className="border-0">Producto</th>
                                    <th className="border-0">Valor</th>
                                    <th className="border-0">Intento</th>
                                    <th className="border-0">En el campo</th>
                                    <th className="border-0">Usuario</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((e) => (
                                    <tr key={e.id} className={isFast(e) ? 'table-danger' : ''}>
                                        <td>{hora(e.enteredAt)}</td>
                                        <td>{e.gapSeconds == null ? '—' : formatDuration(e.gapSeconds)}</td>
                                        <td>{e.name || e.productCode}</td>
                                        <td>
                                            {/* El valor anterior importa: "3 → 7" en el recuento es
                                                alguien que cambió de idea, o que se acomodó. */}
                                            {e.previousQuantity != null && <span className="text-muted">{e.previousQuantity} → </span>}
                                            <strong>{e.quantity == null ? 'borrado' : e.quantity}</strong>
                                        </td>
                                        <td>{Number(e.attemptNo) > 1 ? 'Recuento' : 'Conteo'}</td>
                                        <td>
                                            {e.elapsedMs == null
                                                ? <small className="text-muted">{e.source === 'confirm' ? 'al confirmar' : '—'}</small>
                                                : formatDuration(e.elapsedMs / 1000)}
                                        </td>
                                        <td>
                                            <small>{e.enteredBy || '—'}</small>
                                            {Number(e.enteredRole) === ROLE_ADMIN && <span className="badge badge-secondary ml-1">admin</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }
}

export default StockCountPaceComponent;
